import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Button, Typography } from '@mui/material';
import { AlarmIcon, SortIcon, UserAddIcon } from '@sharedCR/coreDesignSystem/icons';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { LEAD_NOTIFICATION_TIMEOUT_MS } from 'constants/telephony';

import { acceptLeadAction, rejectLeadAction, timeoutLeadAction } from 'store/system/actions';
import {
  selectIsAcceptingLead,
  selectIsLeadLiveTransfer,
  selectIsLeadNotificationVisible,
  selectIsRejectingLead,
  selectLeadData,
  selectLeadNotification,
  selectLeadNotificationId,
} from 'store/system/selectors';

import {
  acceptButtonStyles,
  actionsStyles,
  bodyStyles,
  containerStyles,
  fieldLabelStyles,
  fieldRowStyles,
  fieldValueStyles,
  headerStyles,
  headerTitleStyles,
  liveTransferHeaderStyles,
  overlayStyles,
  rejectButtonStyles,
  timerStyles,
} from './LeadNotification.styles';
import { LeadNotificationTestIds } from './LeadNotification.testIds';

const SECONDS_IN_MS = 1000;

const LeadNotificationField = memo(({ label, value }) => {
  if (!value) {
    return null;
  }

  return (
    <Box sx={fieldRowStyles}>
      <Typography sx={fieldLabelStyles} variant="regular12">
        {label}
      </Typography>
      <Typography sx={fieldValueStyles} variant="regular14">
        {value}
      </Typography>
    </Box>
  );
});

LeadNotificationField.displayName = 'LeadNotificationField';

LeadNotificationField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
};

const LeadNotification = () => {
  const dispatch = useDispatch();
  const isVisible = useSelector(selectIsLeadNotificationVisible);
  const notificationId = useSelector(selectLeadNotificationId);
  const leadData = useSelector(selectLeadData);
  const isLiveTransfer = useSelector(selectIsLeadLiveTransfer);
  const isAccepting = useSelector(selectIsAcceptingLead);
  const isRejecting = useSelector(selectIsRejectingLead);
  const { receivedAt } = useSelector(selectLeadNotification);
  const timeoutRef = useRef(null);
  const intervalRef = useRef(null);

  const getRemainingSeconds = useCallback(() => {
    if (!receivedAt) {
      return Math.round(LEAD_NOTIFICATION_TIMEOUT_MS / SECONDS_IN_MS);
    }

    const elapsed = Date.now() - receivedAt;

    return Math.max(0, Math.ceil((LEAD_NOTIFICATION_TIMEOUT_MS - elapsed) / SECONDS_IN_MS));
  }, [receivedAt]);

  const [remainingSeconds, setRemainingSeconds] = useState(getRemainingSeconds);

  useEffect(() => {
    if (!isVisible) {
      return;
    }

    setRemainingSeconds(getRemainingSeconds());

    intervalRef.current = setInterval(() => {
      setRemainingSeconds(getRemainingSeconds());
    }, SECONDS_IN_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isVisible, getRemainingSeconds]);

  const currentHeaderStyles = useMemo(
    () => (isLiveTransfer ? liveTransferHeaderStyles : headerStyles),
    [isLiveTransfer],
  );

  const onAccept = useCallback(() => {
    if (notificationId) {
      dispatch(acceptLeadAction({ notificationId }));
    }
  }, [dispatch, notificationId]);

  const onReject = useCallback(() => {
    if (notificationId) {
      dispatch(rejectLeadAction({ notificationId, reason: 'UserRejected' }));
    }
  }, [dispatch, notificationId]);

  useEffect(() => {
    if (!isVisible || !notificationId || !receivedAt) {
      return;
    }

    // Cancel the timeout as soon as the user clicks Accept or Reject to prevent
    // a race where the timeout fires while the API call is still in-flight.
    if (isAccepting || isRejecting) {
      return;
    }

    const elapsed = Date.now() - receivedAt;
    const remaining = Math.max(0, LEAD_NOTIFICATION_TIMEOUT_MS - elapsed);

    timeoutRef.current = setTimeout(() => {
      dispatch(timeoutLeadAction({ notificationId }));
    }, remaining);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [dispatch, isVisible, notificationId, receivedAt, isAccepting, isRejecting]);

  if (!isVisible || !leadData) {
    return null;
  }

  const borrowerName = leadData.borrowerName ?? 'Unknown';
  const isProcessing = isAccepting || isRejecting;

  return ReactDOM.createPortal(
    <Box sx={overlayStyles}>
      <Box sx={containerStyles}>
        <Box sx={currentHeaderStyles}>
          <Box sx={headerTitleStyles}>
            {isLiveTransfer ? <SortIcon /> : <UserAddIcon />}
            <Typography variant="bold16">
              {isLiveTransfer ? 'Live Transfer' : 'Incoming Lead'}
            </Typography>
          </Box>
          <Box sx={timerStyles}>
            <AlarmIcon size={IconSize[16]} />
            <Typography variant="regular12">{remainingSeconds}s</Typography>
          </Box>
        </Box>

        <Box sx={bodyStyles}>
          <LeadNotificationField label="Borrower" value={borrowerName} />
          <LeadNotificationField label="Phone" value={leadData.phoneNumber} />
          <LeadNotificationField label="Loan Amount" value={leadData.loanAmount} />
          <LeadNotificationField label="State" value={leadData.state} />
          <LeadNotificationField label="Source" value={leadData.source} />
        </Box>

        <Box sx={actionsStyles}>
          <Button
            color="success"
            data-testid={LeadNotificationTestIds.ACCEPT}
            disabled={isProcessing}
            sx={acceptButtonStyles}
            variant="contained"
            onClick={onAccept}
          >
            {isAccepting ? 'Accepting...' : 'Accept'}
          </Button>
          <Button
            color="error"
            data-testid={LeadNotificationTestIds.REJECT}
            disabled={isProcessing}
            sx={rejectButtonStyles}
            variant="outlined"
            onClick={onReject}
          >
            {isRejecting ? 'Rejecting...' : 'Reject'}
          </Button>
        </Box>
      </Box>
    </Box>,
    document.body,
  );
};

export default memo(LeadNotification);
